import { useCallback } from 'react'

import { isClientError } from '@tribeplatform/gql-client/lib'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { Link } from '@tribeplatform/react-components/Link'
import { useNominateNewDomain } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { HelperText } from '@tribeplatform/react-ui-kit/HelperText'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

export const ApexWarning = (props: { domain: string }) => {
  const { domain } = props
  const { $t } = useI18n()
  const recommendedDomain = `www.${domain}`
  const { mutateAsync: nominateDomain, isLoading: isNominating } =
    useNominateNewDomain()

  const cloudflareLink = (
    <Link variant="accent" href="https://www.cloudflare.com/">
      {/* eslint-disable-next-line react/jsx-no-literals */}
      <strong>Cloudflare</strong>
    </Link>
  )

  const handleSwitch = useCallback(async () => {
    try {
      await nominateDomain({ domain: recommendedDomain })
    } catch (error) {
      const description =
        (isClientError(error) && error.response.errors.at(0).message) ??
        $t({
          id: 'Generics.SomethingWentWrong',
          defaultMessage: 'Something went wrong',
        })
      toast({
        title: 'Error',
        status: 'error',
        description,
      })
      // No-op. Handled in form using the error state.
    }
  }, [recommendedDomain, nominateDomain, $t])
  return (
    <HelperText status="warn" highlighted className="flex flex-col space-y-2">
      <p>
        <T
          id="Admin.DomainTransfer.ApexDomainRestrictionsWarning"
          defaultMessage="You’re trying to move your site to a <strong>root domain</strong> and adding CNAME records to root domains are prohibited by DNS standards. Some DNS providers offer workarounds for this restriction, but not all of these solutions are compatible with our platform. At the moment, we can only recommend {cloudflare} when associating a root domain with your <strong>{BRAND}</strong> site."
          values={{
            apex_domain: domain,
            cloudflare: cloudflareLink,
          }}
        />
      </p>
      <p>
        <T
          id="Admin.DomainTransfer.ApexOption.SubdomainWithAction"
          defaultMessage="If you’re using another provider and unable to switch to {cloudflare}, consider switching to {recommended_domain} subdomain, instead of {domain}."
          values={{
            cloudflare: cloudflareLink,
            recommended_domain: (
              <strong className="font-mono">{recommendedDomain}</strong>
            ),
            domain: <strong className="font-mono">{domain}</strong>,
          }}
        />
      </p>
      <div className="flex justify-end">
        <Button
          variant="plainNeutral"
          loading={isNominating}
          onClick={() => {
            handleSwitch()
          }}
        >
          <T
            id="Admin.DomainTransfer.ApexOption.Action"
            defaultMessage="Continue with {recommended_domain}"
            values={{
              recommended_domain: (
                <strong className="font-mono">{recommendedDomain}</strong>
              ),
            }}
          />
        </Button>
      </div>
    </HelperText>
  )
}
